/**
 * Capa de analytics del embudo de autenticación y conversión del Club Maja.
 *
 * ARQUITECTURA ACTUAL (Etapa 1.1):
 *   - Eventos POST-login: se escriben directamente en Firestore analytics_eventos
 *     con uid del usuario autenticado. Las reglas Firestore validan esquema.
 *   - Eventos PRE-login (login_view, login_google_started, etc.): se intentan
 *     escribir solo si Firebase Auth tiene un usuario (anónimo o real). Si no
 *     hay sesión activa, el evento se loguea en consola pero NO se escribe en
 *     Firestore — las reglas rechazan escrituras no autenticadas.
 *
 * PRÓXIMO PASO recomendado (cuando el volumen lo justifique):
 *   Migrar trackEvento a una Cloud Function callable que:
 *     - valide el evento contra whitelist
 *     - aplique rate limiting por IP/uid
 *     - escriba desde el servidor (sin exponer reglas de cliente)
 *   Esto permitirá trackear eventos pre-login sin abrir Firestore públicamente.
 *
 * NUNCA incluir datos sensibles: DNI, teléfono, email, contraseña, puntos exactos.
 */
import { db, auth } from '../firebase'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'

const DEV = process.env.NODE_ENV === 'development'

export const EVENTOS = {
  // Autenticación
  LOGIN_VIEW:                   'login_view',
  LOGIN_EMAIL_STARTED:          'login_email_started',
  LOGIN_EMAIL_SUCCESS:          'login_email_success',
  LOGIN_EMAIL_FAILED:           'login_email_failed',
  LOGIN_GOOGLE_STARTED:         'login_google_started',
  LOGIN_GOOGLE_SUCCESS:         'login_google_success',
  LOGIN_GOOGLE_FAILED:          'login_google_failed',
  PASSWORD_RECOVERY_STARTED:    'password_recovery_started',
  PASSWORD_RECOVERY_SENT:       'password_recovery_sent',

  // Onboarding Google
  GOOGLE_ONBOARDING_STARTED:    'google_onboarding_started',
  GOOGLE_ONBOARDING_COMPLETED:  'google_onboarding_completed',
  GOOGLE_ONBOARDING_ABANDONED:  'google_onboarding_abandoned',

  // Vinculación de cuenta existente
  ACCOUNT_LINK_OFFERED:         'account_link_offered',
  ACCOUNT_LINK_COMPLETED:       'account_link_completed',
  ACCOUNT_LINK_REJECTED:        'account_link_rejected',

  // Registro email
  REGISTRATION_VIEW:            'registration_view',
  REGISTRATION_STARTED:         'registration_started',
  REGISTRATION_COMPLETED:       'registration_completed',
  REGISTRATION_FAILED:          'registration_failed',

  // Perfil
  PROFILE_INCOMPLETE_DETECTED:  'profile_incomplete_detected',
  TELEFONO_DUPLICADO_DETECTADO: 'telefono_duplicado_detectado',

  // Futuras etapas
  POINTS_QUERY_BOT:             'points_query_bot',
  BENEFIT_VIEWED:               'benefit_viewed',
  REDEEM_STARTED:               'redeem_started',
  REDEEM_COMPLETED:             'redeem_completed',
}

// Whitelist de eventos válidos — previene escritura de eventos arbitrarios
const EVENTOS_VALIDOS = new Set(Object.values(EVENTOS))

/**
 * @param {string} evento — uno de EVENTOS.*
 * @param {object} [params] — datos extra NO sensibles (ej: { metodo: 'google' })
 */
export async function trackEvento(evento, params = {}) {
  if (!EVENTOS_VALIDOS.has(evento)) {
    if (DEV) console.warn(`[Analytics] Evento desconocido ignorado: "${evento}"`)
    return
  }
  if (DEV) console.info(`[Analytics] ${evento}`, params)

  const currentUser = auth.currentUser
  if (!currentUser) {
    // Sin sesión → loguear localmente pero no intentar escribir en Firestore
    // (las reglas rechazarían la escritura y generarían error innecesario)
    if (DEV) console.info(`[Analytics] evento pre-login no persistido (sin auth): ${evento}`)
    return
  }

  try {
    await addDoc(collection(db, 'analytics_eventos'), {
      evento,
      params,
      uid: currentUser.uid,   // permite correlacionar embudo por usuario
      timestamp: serverTimestamp(),
    })
  } catch {
    // silencioso — el tracking nunca debe interrumpir el flujo del usuario
  }
}
